import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    Search, LayoutGrid, Activity, UserCircle, LogOut, BarChart3, ChevronLeft, ChevronRight,
    Folder, X, Minus, ChevronDown, FolderPlus, Trash2, Settings
} from 'lucide-react';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth, getStoredUser } from '../../context/AuthContext';
import { usePreferences, getUserActiveModuleCodes } from '../../context/PreferencesContext';
import logo from '../../assets/logo.png';
import {
    getModuleNavMap,
    getNavToModuleMap,
    getModulesList
} from '../../services/modulesService';
import { useModuleCounts } from '../../hooks/useModuleCounts';
import { getIconByName, findModuleByNavId, findModuleByCode } from '../../utils/iconUtils';
import MouseGlow from '../ui/MouseGlow';
import { Tooltip, Modal } from '../ui';
import NotificationBell from '../notifications/NotificationBell';
import { getProfilePhoto, getFileUrl } from '../../services/filesService';

// Helper to get navId from code using dynamic map
const getNavIdFromCode = (code) => {
    const navMap = getModuleNavMap();
    return navMap[code] || code?.toLowerCase();
};

// Helper to get code from navId using dynamic map
const getCodeFromNavId = (navId) => {
    const map = getNavToModuleMap();
    return map[navId];
};

// Fixed dock apps (always present, not movable)
const DOCK_APPS = [
    { id: 'dashboard', icon: BarChart3, color: '#8b5cf6', navId: 'dashboard' },
    { id: 'modules', icon: LayoutGrid, color: '#6366f1', navId: 'modules' },
    { id: 'search', icon: Search, color: '#f59e0b', navId: 'search' },
    { id: 'profile', icon: UserCircle, color: '#3b82f6', navId: 'profile' },
    { id: 'activities', icon: Activity, color: '#10b981', navId: 'activities' }
];

// Default gradient fallback
const DEFAULT_GRADIENT = 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)';

// Helper to normalize keys to lowercase
const normalizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    return Object.keys(obj).reduce((acc, key) => {
        const lowerKey = key.toLowerCase();
        acc[lowerKey] = normalizeKeys(obj[key]);
        return acc;
    }, {});
};

/**
 * Remove duplicates from desktops array
 * Each item should only appear once across all desktops
 * @param {Array} desktopsArray - Array of arrays of item identifiers
 * @returns {Object} { cleaned: cleanedDesktops, hadDuplicates: boolean }
 */
const deduplicateDesktops = (desktopsArray) => {
    if (!desktopsArray || !Array.isArray(desktopsArray)) return { cleaned: [[]], hadDuplicates: false };

    const seen = new Set();
    let hadDuplicates = false;

    const cleaned = desktopsArray.map(desktop => {
        if (!Array.isArray(desktop)) return [];
        return desktop.filter(itemId => {
            if (seen.has(itemId)) {
                hadDuplicates = true;
                console.warn('[Home] Duplicate item removed:', itemId);
                return false;
            }
            seen.add(itemId);
            return true;
        });
    }).filter((desktop, index) => index === 0 || desktop.length > 0); // Keep first desktop even if empty

    return { cleaned: cleaned.length > 0 ? cleaned : [[]], hadDuplicates };
};

/**
 * Remove duplicates from pinnedItems array
 * @param {Array} items - Array of pinned items {type, id}
 * @returns {Object} { cleaned: cleanedItems, hadDuplicates: boolean }
 */
const deduplicatePinnedItems = (items) => {
    if (!items || !Array.isArray(items)) return { cleaned: [], hadDuplicates: false };

    const seen = new Set();
    let hadDuplicates = false;

    const cleaned = items.filter(item => {
        const key = `${item.type}:${item.id}`;
        if (seen.has(key)) {
            hadDuplicates = true;
            console.warn('[Home] Duplicate pinned item removed:', key);
            return false;
        }
        seen.add(key);
        return true;
    });

    return { cleaned, hadDuplicates };
};

/**
 * Dashboard Language Switcher - Style adapté au dashboard
 */
const DashboardLanguageSwitcher = () => {
    const { language, setLanguage, languages } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (langCode) => {
        setLanguage(langCode);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors"
            >
                <span className="uppercase">{currentLanguage.code}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-1 min-w-[150px] z-[9999]">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${lang.code === language ? 'bg-purple-50 text-purple-600 font-medium' : 'text-gray-700'
                                }`}
                        >
                            <span className="w-5 text-center font-medium text-xs uppercase opacity-60">{lang.flag}</span>
                            <span>{lang.name}</span>
                            {lang.code === language && (
                                <span className="ml-auto w-2 h-2 rounded-full bg-purple-500" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Group Popup Modal - Shows modules inside a group
 */
const GroupPopupModal = ({ group, onClose, onNavigate, moduleCounts, t, language, onRemoveFromGroup, onDeleteGroup, activeModuleCodes }) => {
    const popupRef = useRef(null);
    const [draggingModule, setDraggingModule] = useState(null);
    const dragDataRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Don't close if we're dragging
            if (draggingModule) return;
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose, draggingModule]);

    if (!group) return null;

    // Filter modules that are pinned/visible AND active in subscription
    const visibleModules = (group.modules || []).filter(m => {
        const isPinned = m.ispinned === undefined || m.ispinned === null ||
            m.ispinned === true || m.ispinned === 1 || m.ispinned === '1';
        const moduleCode = (m.code || '').toUpperCase();
        const isActiveInSubscription = activeModuleCodes.has(moduleCode);
        return isPinned && isActiveInSubscription;
    });

    const getNavId = (code) => getNavIdFromCode(code);

    // Handle drag start for module
    const handleModuleDragStart = (e, module) => {
        const navId = getNavId(module.code);
        const dragData = {
            type: 'module-from-group',
            moduleId: module.moduleid || module.id,
            moduleCode: module.code,
            navId: navId,
            groupId: group.id
        };
        dragDataRef.current = dragData;
        e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'move';
        setDraggingModule(module.moduleid || module.id);

        // Clone the icon element for drag image
        const iconElement = e.currentTarget.querySelector('[data-drag-icon]');
        if (iconElement) {
            const clone = iconElement.cloneNode(true);
            clone.style.position = 'fixed';
            clone.style.top = '-1000px';
            clone.style.left = '-1000px';
            clone.style.zIndex = '99999';
            clone.style.pointerEvents = 'none';
            document.body.appendChild(clone);
            e.dataTransfer.setDragImage(clone, 28, 28);

            // Remove clone after drag starts
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (clone.parentNode) {
                        document.body.removeChild(clone);
                    }
                }, 0);
            });
        }
    };

    // Handle drag end - check if dropped outside the popup
    const handleModuleDragEnd = (e) => {
        const moduleId = draggingModule;
        setDraggingModule(null);

        // Check if the drop happened outside the popup
        if (popupRef.current && dragDataRef.current) {
            const rect = popupRef.current.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;

            // If dropped outside the popup, remove from group
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                if (onRemoveFromGroup) {
                    onRemoveFromGroup(dragDataRef.current);
                }
            }
        }
        dragDataRef.current = null;
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
            <div
                ref={popupRef}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col overflow-hidden"
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-4 text-white bg-cabaza-purple"
                    style={group.color ? { backgroundColor: group.color } : undefined}
                >
                    <div className="flex items-center gap-3">
                        <Folder className="w-6 h-6" />
                        <span className="font-semibold text-lg">{group.name}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Modules Grid */}
                <div className="p-4 overflow-y-auto flex-1">
                    {visibleModules.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-400 mb-4">
                                {t.modules?.noModules || 'No modules in this group'}
                            </p>
                            {onDeleteGroup && (
                                <button
                                    onClick={() => onDeleteGroup(group.id)}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    <span className="font-medium">{t.dashboard?.deleteFolder || 'Delete folder'}</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-3">
                            {visibleModules.map((module) => {
                                // Get icon from module data (from DB)
                                const iconName = module.icon || module.ICON;
                                const moduleCode = (module.code || module.CODE || '').toUpperCase();
                                const Icon = getIconByName(iconName || moduleCode, LayoutGrid);
                                const navId = getNavId(module.code);
                                const color = module.color || module.COLOR || '#8b5cf6';
                                const badgeCount = moduleCounts[moduleCode] || 0;
                                const hasBadge = badgeCount > 0;
                                const isDragging = draggingModule === (module.moduleid || module.id);

                                return (
                                    <div
                                        key={module.itemid || module.moduleid}
                                        draggable
                                        onDragStart={(e) => handleModuleDragStart(e, module)}
                                        onDragEnd={handleModuleDragEnd}
                                        onClick={() => {
                                            if (!draggingModule) {
                                                onNavigate(navId);
                                                onClose();
                                            }
                                        }}
                                        className={`flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-gray-100 transition-all cursor-pointer select-none
                                            ${isDragging ? 'opacity-40 scale-95' : ''}`}
                                    >
                                        <div
                                            data-drag-icon
                                            className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
                                            style={{ backgroundColor: color }}
                                        >
                                            <Icon className="w-7 h-7 text-white" />
                                            {hasBadge && (
                                                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-white text-gray-700 text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                                    {badgeCount > 9 ? '9+' : badgeCount}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-700 font-medium text-center leading-tight max-w-[70px] truncate">
                                            {module[`name_${language}`] || module.name_fr || module.name || module.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Hint for drag */}
                <div className="px-4 pb-4">
                    <p className="text-xs text-gray-400 text-center">
                        {t.modules?.dragToRemove || 'Drag a module outside to remove from folder'}
                    </p>
                </div>
            </div>
        </div>,
        document.body
    );
};

/**
 * Dashboard - Smartphone-style home screen with app icons
 */
const Dashboard = ({ onNavigate }) => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { t, language } = useTranslation();
    const { showLanguageSwitcher } = useLanguage();
    const { currentTheme } = useTheme();
    const user = getStoredUser();
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Use centralized preferences context
    const {
        pinnedItems: contextPinnedItems,
        dashboardDesktops: contextDashboardDesktops,
        moduleGroups: contextModuleGroups,
        updatePinnedItems: contextUpdatePinnedItems,
        updateDashboardDesktops: contextUpdateDashboardDesktops,
        updateModuleGroups: contextUpdateModuleGroups,
        refreshModuleGroups,
        isLoaded: preferencesLoaded
    } = usePreferences();

    // Local state for immediate UI updates (synced with context)
    const [pinnedItems, setPinnedItems] = useState(() => {
        const stored = localStorage.getItem('cabazaPinnedItems');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                const { cleaned } = deduplicatePinnedItems(parsed);
                return cleaned;
            } catch (e) { }
        }
        return [];
    });

    // Sync local state with context only on initial load (not on every change)
    const pinnedItemsInitializedRef = useRef(false);
    useEffect(() => {
        if (preferencesLoaded && !pinnedItemsInitializedRef.current && contextPinnedItems && contextPinnedItems.length > 0) {
            const { cleaned } = deduplicatePinnedItems(contextPinnedItems);
            setPinnedItems(cleaned);
            pinnedItemsInitializedRef.current = true;
        }
    }, [contextPinnedItems, preferencesLoaded]);

    // Groups data from context (with local state for immediate updates)
    const [groupsData, setGroupsData] = useState(() => {
        // Read from new preferences cache
        try {
            const cached = localStorage.getItem('cabazaPreferencesCache');
            if (cached) {
                const prefs = JSON.parse(cached);
                return prefs.moduleGroups || [];
            }
        } catch (e) {}
        return [];
    });

    // Sync groups data with context whenever it changes
    useEffect(() => {
        if (contextModuleGroups) {
            const normalizedGroups = normalizeKeys(contextModuleGroups);
            setGroupsData(normalizedGroups);
        }
    }, [contextModuleGroups]);

    // All modules from API
    const [allModules, setAllModules] = useState([]);

    // Loading state - true until data is fully loaded
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    // Module counts for badges
    const moduleCounts = useModuleCounts();

    // Dock profile photo
    const [dockProfilePhotoUrl, setDockProfilePhotoUrl] = useState(null);
    useEffect(() => {
        const loadDockPhoto = async () => {
            try {
                const userUuid = localStorage.getItem('userUuid');
                if (userUuid) {
                    const result = await getProfilePhoto(userUuid);
                    const success = result.success ?? result.SUCCESS;
                    const data = result.data ?? result.DATA;
                    if (success && data) {
                        const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                        if (filePath) setDockProfilePhotoUrl(getFileUrl(filePath));
                    }
                }
            } catch (err) { /* ignore */ }
        };
        loadDockPhoto();
    }, []);

    // Open group popup
    const [openGroupPopup, setOpenGroupPopup] = useState(null);

    // Dashboard layout system:
    // - itemsOrder: global order of all items (flat array) - used for volatile items
    // - attachedItems: items fixed to a specific desktop { itemId: desktopIndex }
    // - desktops: legacy format for DB compatibility

    const [itemsOrder, setItemsOrder] = useState(() => {
        const stored = localStorage.getItem('cabazaDashboardItemsOrder');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    return [...new Set(parsed)]; // Deduplicate
                }
            } catch (e) { }
        }
        // Fallback: try to get from old desktops format
        const oldDesktops = localStorage.getItem('cabazaDashboardDesktops');
        if (oldDesktops) {
            try {
                const parsed = JSON.parse(oldDesktops);
                if (Array.isArray(parsed)) {
                    return [...new Set(parsed.flat())];
                }
            } catch (e) { }
        }
        return [];
    });

    const [attachedItems, setAttachedItems] = useState(() => {
        const stored = localStorage.getItem('cabazaDashboardAttachedItems');
        if (stored) {
            try {
                return JSON.parse(stored) || {};
            } catch (e) { }
        }
        return {};
    });

    // Legacy desktops state for backward compatibility
    const [desktops, setDesktops] = useState(() => {
        const stored = localStorage.getItem('cabazaDashboardDesktops');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    const { cleaned } = deduplicateDesktops(parsed);
                    return cleaned;
                }
            } catch (e) { }
        }
        return [[]];
    });

    // Sync with context on initial load ONLY - file server is source of truth
    // At login AND refresh: load from file server and overwrite localStorage
    // After initial load, local changes should NOT be overwritten by context updates
    const desktopsInitializedRef = useRef(false);
    useEffect(() => {
        if (preferencesLoaded && !desktopsInitializedRef.current) {
            desktopsInitializedRef.current = true;

            // Always use context data (from file server) as source of truth on initial load
            if (contextDashboardDesktops && contextDashboardDesktops.length > 0) {
                const { cleaned } = deduplicateDesktops(contextDashboardDesktops);
                setDesktops(cleaned);
                const allItems = [...new Set(cleaned.flat())];
                setItemsOrder(allItems);
                localStorage.setItem('cabazaDashboardItemsOrder', JSON.stringify(allItems));
                localStorage.setItem('cabazaDashboardDesktops', JSON.stringify(cleaned));
            }
        }
        // Only run on initial load, not when contextDashboardDesktops changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferencesLoaded]);

    // Current desktop index
    const [currentDesktop, setCurrentDesktop] = useState(0);


    // Drag state
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const [draggedId, setDraggedId] = useState(null);

    // Touch drag state
    const [touchDragInfo, setTouchDragInfo] = useState(null);
    const touchStartPos = useRef(null);
    const longPressTimer = useRef(null);
    const appRefs = useRef({});

    // Save timeout ref for debouncing
    const saveTimeoutRef = useRef(null);

    // Edge drag zone state
    const [edgeDragZone, setEdgeDragZone] = useState(null);
    const edgeDragTimer = useRef(null);

    // Drop target state
    const [dropTarget, setDropTarget] = useState(null);

    // Group drop target state (when dragging a module over a group)
    const [groupDropTarget, setGroupDropTarget] = useState(null);

    // Edge hover state (for showing arrows on mouse hover near edges)
    const [edgeHover, setEdgeHover] = useState(null); // 'left' | 'right' | null

    // Create folder dialog state
    const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // Active module codes for subscription filtering (memoized for GroupPopupModal)
    const activeModuleCodes = useMemo(() => new Set(getUserActiveModuleCodes()), []);

    // Track if data has been loaded
    const dataLoadedRef = useRef(false);

    // Load data when preferences are loaded (ensures active modules are available)
    useEffect(() => {
        // Wait for preferences to be loaded before loading dashboard data
        if (!preferencesLoaded) return;
        if (dataLoadedRef.current) return;
        dataLoadedRef.current = true;

        const loadData = async () => {
            try {
                // If no userId, just mark as loaded (will show empty state)
                if (!userId || isNaN(userId)) {
                    setIsDataLoaded(true);
                    return;
                }

                // Get modules from localStorage (synced at login)
                const modulesData = getModulesList();
                const normalizedModules = normalizeKeys(modulesData);

                // Filter to only show modules active for this user's subscription
                const activeModuleCodes = getUserActiveModuleCodes();
                console.log('[Home] Active module codes:', activeModuleCodes.length);
                const filteredModules = activeModuleCodes.length > 0
                    ? normalizedModules.filter(m => {
                        const code = (m.code || '').toUpperCase();
                        return activeModuleCodes.includes(code);
                    })
                    : normalizedModules; // Fallback to all modules if no active modules found

                console.log('[Home] Filtered modules:', filteredModules.length);
                setAllModules(filteredModules);

                // Groups are loaded from PreferencesContext (cached in localStorage)
                // The useEffect hook above syncs local groupsData state with context

                // Mark data as loaded (module counts handled by useModuleCounts hook)
                setIsDataLoaded(true);
            } catch (error) {
                console.error('Error loading dashboard data:', error);
                // Still mark as loaded to show empty state
                setIsDataLoaded(true);
            }
        };
        loadData();
    }, [userId, preferencesLoaded]);

    // Track previous language to detect changes
    const prevLanguageRef = useRef(language);

    // Reload groupsData when language changes (refresh from API to get translated names)
    useEffect(() => {
        if (!userId || !dataLoadedRef.current) return;
        if (prevLanguageRef.current === language) return;

        prevLanguageRef.current = language;

        // Refresh module groups from API with new language
        refreshModuleGroups();
    }, [language, userId, refreshModuleGroups]);

    // Listen for changes from sidebar/modules page
    useEffect(() => {
        const handlePinnedItemsChange = () => {
            const stored = localStorage.getItem('cabazaPinnedItems');
            setPinnedItems(stored ? JSON.parse(stored) : []);
        };

        const handleGroupsChange = () => {
            // Groups are now managed by PreferencesContext via cabazaPreferencesCache
            try {
                const cached = localStorage.getItem('cabazaPreferencesCache');
                if (cached) {
                    const prefs = JSON.parse(cached);
                    if (prefs.moduleGroups) {
                        setGroupsData(normalizeKeys(prefs.moduleGroups));
                    }
                }
            } catch (e) {
                console.error('Error reading groups from preferences cache:', e);
            }
        };

        const handleActiveModulesChange = () => {
            // Re-filter modules when active modules are loaded/changed
            const modulesData = getModulesList();
            const normalizedModules = normalizeKeys(modulesData);
            const activeModuleCodes = getUserActiveModuleCodes();
            const filteredModules = activeModuleCodes.length > 0
                ? normalizedModules.filter(m => {
                    const code = (m.code || '').toUpperCase();
                    return activeModuleCodes.includes(code);
                })
                : normalizedModules;
            setAllModules(filteredModules);
        };

        window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedModulesChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
        window.addEventListener('modulesGroupsChanged', handleGroupsChange);
        window.addEventListener('modulePinnedToggled', handleGroupsChange);
        window.addEventListener('moduleGroupsChanged', handleGroupsChange);
        window.addEventListener('userActiveModulesChanged', handleActiveModulesChange);

        return () => {
            window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedModulesChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
            window.removeEventListener('modulesGroupsChanged', handleGroupsChange);
            window.removeEventListener('modulePinnedToggled', handleGroupsChange);
            window.removeEventListener('moduleGroupsChanged', handleGroupsChange);
            window.removeEventListener('userActiveModulesChanged', handleActiveModulesChange);
        };
    }, []);

    // Dynamic grid dimensions based on available space
    const [gridDimensions, setGridDimensions] = useState({ cols: 4, rows: 3, capacity: 11 });
    const gridContainerRef = useRef(null);

    // Constants for grid calculation
    const ICON_SIZE = 80; // Icon width/height including padding
    const ICON_GAP = 16; // Gap between icons
    const MIN_COLS = 2;
    const MAX_COLS = 8;
    const MIN_ROWS = 1;

    // Calculate grid dimensions based on container size
    const calculateGridDimensions = useCallback(() => {
        if (!gridContainerRef.current) return;

        const container = gridContainerRef.current;
        const rect = container.getBoundingClientRect();

        // Available space (subtract padding)
        const availableWidth = rect.width - 32; // 16px padding each side
        const availableHeight = rect.height - 32;

        // Calculate how many icons fit
        const cols = Math.max(MIN_COLS, Math.min(MAX_COLS, Math.floor((availableWidth + ICON_GAP) / (ICON_SIZE + ICON_GAP))));
        const rows = Math.max(MIN_ROWS, Math.floor((availableHeight + ICON_GAP) / (ICON_SIZE + ICON_GAP)));

        // Total capacity = cols × rows, minus 1 for "New folder" button
        const capacity = Math.max(1, (cols * rows) - 1);

        setGridDimensions(prev => {
            if (prev.cols !== cols || prev.rows !== rows || prev.capacity !== capacity) {
                return { cols, rows, capacity };
            }
            return prev;
        });
    }, []);

    // Listen for resize and calculate dimensions
    useEffect(() => {
        calculateGridDimensions();

        const handleResize = () => {
            calculateGridDimensions();
        };

        window.addEventListener('resize', handleResize);
        // Also recalculate on orientation change for mobile
        window.addEventListener('orientationchange', handleResize);

        // Use ResizeObserver for container-specific changes
        const resizeObserver = new ResizeObserver(handleResize);
        if (gridContainerRef.current) {
            resizeObserver.observe(gridContainerRef.current);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
            resizeObserver.disconnect();
        };
    }, [calculateGridDimensions]);

    // Capacity per desktop (dynamic based on available space)
    const desktopCapacity = gridDimensions.capacity;

    // Sync desktops with pinned items when they change
    // Skip initial sync if we already have data from localStorage/API
    const initialSyncDoneRef = useRef(false);

    // Get modules that are in ANY group (to exclude from individual display)
    const getModulesInGroups = useCallback(() => {
        const modulesInGroups = new Set();
        groupsData.forEach(group => {
            if (group.modules) {
                group.modules.forEach(m => {
                    const navId = getNavIdFromCode(m.code);
                    if (navId) modulesInGroups.add(navId);
                });
            }
        });
        return modulesInGroups;
    }, [groupsData]);

    // Helper to redistribute items into desktops based on current capacity
    const redistributeItems = useCallback((orderedItems, capacity) => {
        if (!orderedItems || orderedItems.length === 0) return [[]];
        if (capacity <= 0) capacity = 12; // Fallback

        const newDesktops = [];
        for (let i = 0; i < orderedItems.length; i += capacity) {
            newDesktops.push(orderedItems.slice(i, i + capacity));
        }
        return newDesktops.length > 0 ? newDesktops : [[]];
    }, []);

    // Sync desktops with pinned items (add/remove only, preserve desktop positions)
    useEffect(() => {
        // Skip if pinnedItems is empty (not loaded yet) or capacity not calculated
        if (pinnedItems.length === 0 || desktopCapacity <= 0) return;

        // Get modules that are in groups (to exclude from individual display)
        const modulesInGroups = getModulesInGroups();

        // Convert pinned items to identifiers, filtering out modules that are in groups
        // Use Set to ensure no duplicates from pinnedItems
        const seenPinnedIds = new Set();
        const pinnedIds = pinnedItems
            .filter(item => {
                if (item.type === 'module') {
                    // Exclude modules that are in a group
                    return !modulesInGroups.has(item.id);
                }
                return true; // Keep groups
            })
            .map(item => `${item.type}:${item.id}`)
            .filter(id => {
                // Deduplicate
                if (seenPinnedIds.has(id)) return false;
                seenPinnedIds.add(id);
                return true;
            });
        const pinnedIdSet = new Set(pinnedIds);

        // Check if we already have layout data
        const hasExistingLayout = desktops.some(d => d.length > 0);

        if (hasExistingLayout && initialSyncDoneRef.current) {
            // PRESERVE desktop structure - only add/remove items, don't redistribute
            // For each desktop, filter out items that are no longer pinned
            const seenInDesktops = new Set();
            const newDesktops = desktops.map(desktop =>
                desktop.filter(id => {
                    if (seenInDesktops.has(id)) return false; // Remove duplicates
                    if (!pinnedIdSet.has(id)) return false; // Remove unpinned items
                    seenInDesktops.add(id);
                    return true;
                })
            );

            // Find items that are pinned but not yet in any desktop
            const unplacedItems = pinnedIds.filter(id => !seenInDesktops.has(id));

            // Add unplaced items to the first desktop that has room, or create new desktop
            if (unplacedItems.length > 0) {
                let itemsToPlace = [...unplacedItems];

                for (let i = 0; i < newDesktops.length && itemsToPlace.length > 0; i++) {
                    const roomAvailable = desktopCapacity - newDesktops[i].length;
                    if (roomAvailable > 0) {
                        const toAdd = itemsToPlace.splice(0, roomAvailable);
                        newDesktops[i] = [...newDesktops[i], ...toAdd];
                    }
                }

                // If still items to place, add to new desktops
                while (itemsToPlace.length > 0) {
                    const toAdd = itemsToPlace.splice(0, desktopCapacity);
                    newDesktops.push(toAdd);
                }
            }

            // Remove empty desktops (except keep at least one)
            const filteredDesktops = newDesktops.filter(d => d.length > 0);
            const finalDesktops = filteredDesktops.length > 0 ? filteredDesktops : [[]];

            // Update state if changed
            if (JSON.stringify(finalDesktops) !== JSON.stringify(desktops)) {
                setDesktops(finalDesktops);
                saveToDb(finalDesktops);

                // Adjust current desktop if needed
                if (currentDesktop >= finalDesktops.length) {
                    setCurrentDesktop(Math.max(0, finalDesktops.length - 1));
                }
            }
        } else if (!hasExistingLayout) {
            // No existing layout - initialize from pinnedItems
            const newDesktops = redistributeItems(pinnedIds, desktopCapacity);
            if (newDesktops.length > 0 && newDesktops[0].length > 0) {
                setDesktops(newDesktops);
                saveToDb(newDesktops);
            }
        }

        initialSyncDoneRef.current = true;
    }, [pinnedItems, desktopCapacity, getModulesInGroups, redistributeItems]);

    // Keep a ref to current desktops
    const desktopsRef = useRef(desktops);
    useEffect(() => {
        desktopsRef.current = desktops;
    }, [desktops]);

    // Calculate display desktops (redistributed for current screen size)
    // This is computed, not stored - the original layout is preserved in `desktops`
    // Calculate display desktops based on itemsOrder, attachedItems, and capacity
    const displayDesktops = React.useMemo(() => {
        if (desktopCapacity <= 0) return [[]];

        // Use itemsOrder if available, otherwise fall back to desktops
        const rawItems = itemsOrder.length > 0 ? itemsOrder : desktops.flat();

        // Filter items to only show modules active in subscription
        const filteredItems = rawItems.filter(itemId => {
            if (!itemId || typeof itemId !== 'string') return false;
            const [type, id] = itemId.split(':');
            if (type === 'group') return true; // Groups are always shown
            // For modules, check if active in subscription
            const navId = id;
            // Find module in allModules (already filtered by subscription)
            // Check both by navId mapping and by direct code comparison
            const moduleData = allModules.find(m => {
                const code = (m.code || m.CODE || '').toUpperCase();
                const moduleNavId = getNavIdFromCode(code);
                return moduleNavId === navId || code.toLowerCase() === navId;
            });
            return !!moduleData;
        });

        // Find modules in subscription that are NOT yet in the items (new modules from upgrade)
        const existingNavIds = new Set(filteredItems.map(itemId => {
            const [type, id] = itemId.split(':');
            return type === 'module' ? id : null;
        }).filter(Boolean));

        // Get modules that are in groups (should not be shown as individual items)
        const modulesInGroups = new Set();
        (groupsData || []).forEach(group => {
            (group.modules || []).forEach(m => {
                const code = (m.code || '').toUpperCase();
                const navId = getNavIdFromCode(code) || code.toLowerCase();
                modulesInGroups.add(navId);
                modulesInGroups.add(code.toLowerCase());
            });
        });

        const newModuleItems = allModules
            .filter(m => {
                const code = (m.code || m.CODE || '').toUpperCase();
                const moduleNavId = getNavIdFromCode(code);
                // Exclude if already in items OR if in a group
                const isInItems = existingNavIds.has(moduleNavId) || existingNavIds.has(code.toLowerCase());
                const isInGroup = modulesInGroups.has(moduleNavId) || modulesInGroups.has(code.toLowerCase());
                return !isInItems && !isInGroup;
            })
            .map(m => {
                const code = (m.code || m.CODE || '').toUpperCase();
                const moduleNavId = getNavIdFromCode(code);
                return `module:${moduleNavId}`;
            });

        // Combine existing items with new modules
        const allItems = [...filteredItems, ...newModuleItems];
        if (allItems.length === 0) return [[]];

        // Step 1: Determine how many desktops we need based on attached items
        let maxAttachedDesktop = 0;
        Object.values(attachedItems).forEach(desktopIdx => {
            if (desktopIdx > maxAttachedDesktop) maxAttachedDesktop = desktopIdx;
        });

        // Step 2: Initialize desktops with attached items
        const result = [];
        for (let i = 0; i <= Math.max(maxAttachedDesktop, 0); i++) {
            result.push([]);
        }

        // Place attached items on their designated desktops (in order they appear in allItems)
        const attachedSet = new Set(Object.keys(attachedItems));
        const placedItems = new Set();

        allItems.forEach(itemId => {
            if (attachedSet.has(itemId)) {
                const desktopIdx = attachedItems[itemId];
                // Ensure desktop exists
                while (result.length <= desktopIdx) {
                    result.push([]);
                }
                if (result[desktopIdx].length < desktopCapacity) {
                    result[desktopIdx].push(itemId);
                    placedItems.add(itemId);
                }
            }
        });

        // Step 3: Distribute volatile items (not attached) in remaining space
        const volatileItems = allItems.filter(itemId => !attachedSet.has(itemId));

        let currentDesktopIdx = 0;
        for (const itemId of volatileItems) {
            // Find a desktop with space, starting from currentDesktopIdx
            while (currentDesktopIdx < result.length && result[currentDesktopIdx].length >= desktopCapacity) {
                currentDesktopIdx++;
            }

            // If no existing desktop has space, create a new one
            if (currentDesktopIdx >= result.length) {
                result.push([]);
            }

            result[currentDesktopIdx].push(itemId);
        }

        // Remove trailing empty desktops (but keep at least one)
        while (result.length > 1 && result[result.length - 1].length === 0) {
            result.pop();
        }

        return result.length > 0 ? result : [[]];
    }, [itemsOrder, attachedItems, desktopCapacity, desktops, allModules, groupsData]);

    // Get current desktop apps (from display desktops, which may be redistributed for screen size)
    const gridApps = displayDesktops[currentDesktop] || [];

    // Adjust current desktop index if it's out of bounds for display desktops
    useEffect(() => {
        if (currentDesktop >= displayDesktops.length) {
            setCurrentDesktop(Math.max(0, displayDesktops.length - 1));
        }
    }, [displayDesktops.length, currentDesktop]);

    // Prevent scrolling while dragging
    useEffect(() => {
        const preventScroll = (e) => {
            if (touchDragInfo) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', preventScroll, { passive: false });
        return () => document.removeEventListener('touchmove', preventScroll);
    }, [touchDragInfo]);

    // Save layout to localStorage and DB
    const saveLayout = useCallback((newItemsOrder, newAttachedItems) => {
        // Update states
        setItemsOrder(newItemsOrder);
        setAttachedItems(newAttachedItems);

        // Save to localStorage
        localStorage.setItem('cabazaDashboardItemsOrder', JSON.stringify(newItemsOrder));
        localStorage.setItem('cabazaDashboardAttachedItems', JSON.stringify(newAttachedItems));

        // Also save in old format for backward compatibility with context/DB
        // Reconstruct desktops array from new format for DB storage
        const desktopsForDb = [];
        let maxDesktop = 0;
        Object.values(newAttachedItems).forEach(idx => {
            if (idx > maxDesktop) maxDesktop = idx;
        });

        for (let i = 0; i <= maxDesktop; i++) {
            desktopsForDb.push([]);
        }

        // Place items in order
        const attachedSet = new Set(Object.keys(newAttachedItems));
        newItemsOrder.forEach(itemId => {
            if (attachedSet.has(itemId)) {
                const idx = newAttachedItems[itemId];
                if (desktopsForDb[idx]) {
                    desktopsForDb[idx].push(itemId);
                }
            }
        });

        // Add volatile items to first desktop (they'll redistribute on load)
        const volatileItems = newItemsOrder.filter(id => !attachedSet.has(id));
        if (desktopsForDb.length === 0) desktopsForDb.push([]);
        desktopsForDb[0] = [...desktopsForDb[0], ...volatileItems];

        setDesktops(desktopsForDb);
        contextUpdateDashboardDesktops(desktopsForDb);
    }, [contextUpdateDashboardDesktops]);

    // Remove an item from itemsOrder and attachedItems
    const removeItemFromLayout = useCallback((itemId) => {
        const newOrder = itemsOrder.filter(id => id !== itemId);
        const newAttached = { ...attachedItems };
        delete newAttached[itemId];
        saveLayout(newOrder, newAttached);
    }, [itemsOrder, attachedItems, saveLayout]);

    // Save when reordering within same desktop (doesn't change attachment)
    const saveOrderChange = useCallback((newDisplayDesktops) => {
        // Extract new order from display desktops
        const newOrder = newDisplayDesktops.flat();
        setItemsOrder(newOrder);
        localStorage.setItem('cabazaDashboardItemsOrder', JSON.stringify(newOrder));

        // Update desktops for DB
        setDesktops(newDisplayDesktops);
        contextUpdateDashboardDesktops(newDisplayDesktops);

        // Notify sidebar and modules page of order change
        window.dispatchEvent(new CustomEvent('dashboardOrderChanged'));
    }, [contextUpdateDashboardDesktops]);

    // Legacy saveToDb for compatibility
    const saveToDb = useCallback((newDisplayDesktops) => {
        saveOrderChange(newDisplayDesktops);
    }, [saveOrderChange]);

    // Get background from theme (same as sidebar)
    const hasImageBg = currentTheme?.sidebarBgImage;
    const themeGradient = (currentTheme?.primaryColor && currentTheme?.secondaryColor)
        ? `linear-gradient(135deg, ${currentTheme.primaryColor} 0%, ${currentTheme.secondaryColor} 100%)`
        : DEFAULT_GRADIENT;

    // Handle logout
    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Get group by id
    const getGroupById = (groupId) => {
        return groupsData.find(g => g.id === groupId || g.id === parseInt(groupId));
    };

    // Get module label
    const getModuleLabel = (navId) => {
        // Get module data from DB and use translated name
        const moduleData = findModuleByNavId(navId, allModules);
        if (moduleData) {
            const langKey = `name_${language}`;
            return moduleData[langKey] || moduleData.name_fr || moduleData.NAME_FR || moduleData.code || navId;
        }
        return navId;
    };

    // Get dock app label
    const getDockAppLabel = (appId) => {
        const labels = {
            dashboard: t.menu?.dashboard || 'Dashboard',
            profile: t.menu?.profile || 'Profile',
            activities: t.menu?.activities || 'Activities',
            search: t.menu?.search || 'Search',
            modules: t.menu?.modules || 'Modules'
        };
        return labels[appId] || appId;
    };

    // Parse item identifier (e.g., "module:contacts" or "group:123")
    const parseItemId = (itemId) => {
        if (!itemId || typeof itemId !== 'string') return null;
        const [type, id] = itemId.split(':');
        return { type, id: type === 'group' ? parseInt(id) : id };
    };

    // ========== DRAG & DROP (grid only) ==========

    const handleDragStart = (e, position) => {
        dragItem.current = { position };
        setDraggedId(gridApps[position]);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, position) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        const side = e.clientX < midX ? 'left' : 'right';
        dragOverItem.current = { position, side };
        setDropTarget({ position, side });
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDropTarget(null);
        }
    };

    const handleContainerDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dropTarget) {
            dragOverItem.current = { position: gridApps.length, side: 'left' };
            setDropTarget({ position: gridApps.length, side: 'left' });
        }
        // Check edge zones for desktop drag
        if (draggedId) {
            checkEdgeDrag(e.clientX);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();

        // Check if this is a module being dropped from a group
        try {
            const dropData = e.dataTransfer.getData('text/plain');
            if (dropData) {
                const parsed = JSON.parse(dropData);
                if (parsed.type === 'module-from-group') {
                    handleModuleDropFromGroup(parsed);
                    resetDrag();
                    return;
                }
            }
        } catch (err) {
            // Not JSON data, continue with normal drop handling
        }

        if (!dragItem.current) {
            console.log('[Home] handleDrop: no dragItem.current');
            resetDrag();
            return;
        }

        const from = dragItem.current;
        const to = dragOverItem.current;

        console.log('[Home] handleDrop: from=', from, 'to=', to, 'dropTarget=', dropTarget);
        console.log('[Home] handleDrop: gridApps before=', [...gridApps]);

        // If no valid drop target, cancel
        if (!to) {
            console.log('[Home] handleDrop: no valid drop target (to is null)');
            resetDrag();
            return;
        }

        let insertPosition = to.position;
        if (to.side === 'right') insertPosition = to.position + 1;

        // Check if the item would stay in the same position after move
        const wouldStayInPlace = (from.position === insertPosition) ||
            (to.side === 'left' && to.position === from.position + 1) ||
            (to.side === 'right' && to.position === from.position);

        if (wouldStayInPlace) {
            resetDrag();
            return;
        }

        // Work with displayDesktops (which may be redistributed for screen size)
        let newDisplayDesktops = displayDesktops.map(d => [...d]);
        let newGridApps = [...newDisplayDesktops[currentDesktop]];

        const item = newGridApps[from.position];
        newGridApps.splice(from.position, 1);
        const adjustedPos = from.position < insertPosition ? insertPosition - 1 : insertPosition;
        newGridApps.splice(adjustedPos, 0, item);

        console.log('[Home] handleDrop: item moved=', item, 'from', from.position, 'to', adjustedPos);
        console.log('[Home] handleDrop: newGridApps after=', [...newGridApps]);

        newDisplayDesktops[currentDesktop] = newGridApps;
        // Don't call setDesktops here - saveToDb -> saveOrderChange will do it
        // This prevents a re-render with stale itemsOrder before the new order is set
        saveToDb(newDisplayDesktops);
        resetDrag();
    };

    const resetDrag = () => {
        dragItem.current = null;
        dragOverItem.current = null;
        setDraggedId(null);
        setDropTarget(null);
        setGroupDropTarget(null);
        setEdgeDragZone(null);
        if (edgeDragTimer.current) {
            clearTimeout(edgeDragTimer.current);
            edgeDragTimer.current = null;
        }
    };

    const handleDragEnd = () => {
        // If dropped on edge zone, move to that desktop immediately
        if (edgeDragZone && draggedId && dragItem.current) {
            const targetDesktop = edgeDragZone === 'left'
                ? currentDesktop - 1
                : currentDesktop + 1;
            if (targetDesktop >= 0) {
                moveAppToDesktop(draggedId, dragItem.current.position, targetDesktop);
                return;
            }
        }
        resetDrag();
    };

    // Handle dropping a module from a group onto the dashboard - LOCAL ONLY, no API
    const handleModuleDropFromGroup = (dropData) => {
        const { moduleId, moduleCode, navId, groupId } = dropData;

        // 1. Update groupsData to reflect the removal (LOCAL)
        const updatedGroupsData = groupsData.map(g => {
            if (g.id === groupId || g.id === parseInt(groupId)) {
                return {
                    ...g,
                    modules: (g.modules || []).filter(m =>
                        (m.moduleid || m.id) !== moduleId &&
                        (m.moduleid || m.id) !== parseInt(moduleId)
                    )
                };
            }
            return g;
        });
        setGroupsData(updatedGroupsData);
        contextUpdateModuleGroups(updatedGroupsData); // Save via context

        // 2. Add module to pinnedItems as individual item (if not already there)
        const newPinnedItem = { type: 'module', id: navId };
        const alreadyPinned = pinnedItems.some(item => item.type === 'module' && item.id === navId);
        const updatedPinnedItems = alreadyPinned ? pinnedItems : [...pinnedItems, newPinnedItem];
        setPinnedItems(updatedPinnedItems);
        localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

        // 3. Add module to current desktop (if not already there)
        const moduleIdentifier = `module:${navId}`;
        let newDisplayDesktops = displayDesktops.map(d => [...d]);
        const alreadyOnDesktop = newDisplayDesktops.some(d => d.includes(moduleIdentifier));

        if (!alreadyOnDesktop) {
            if (newDisplayDesktops[currentDesktop].length < desktopCapacity) {
                newDisplayDesktops[currentDesktop].push(moduleIdentifier);
            } else {
                let placed = false;
                for (let i = 0; i < newDisplayDesktops.length; i++) {
                    if (newDisplayDesktops[i].length < desktopCapacity) {
                        newDisplayDesktops[i].push(moduleIdentifier);
                        placed = true;
                        break;
                    }
                }
                if (!placed) {
                    newDisplayDesktops.push([moduleIdentifier]);
                }
            }
        }

        setDesktops(newDisplayDesktops);
        saveToDb(newDisplayDesktops);

        // 4. Close the group popup
        setOpenGroupPopup(null);

        // 5. Dispatch events to sync with other components
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
        window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));

        // 6. Save pinnedItems via context
        if (userId) {
            contextUpdatePinnedItems(updatedPinnedItems);
        }
    };

    // Handle dropping a module onto a group (to add it to the group) - LOCAL ONLY, no API
    const handleDropModuleOnGroup = (moduleNavId, modulePosition, targetGroupId) => {
        // Get the module code from navId
        const moduleCode = getCodeFromNavId(moduleNavId);
        if (!moduleCode) {
            console.error('Unknown module navId:', moduleNavId);
            return;
        }

        // Find the module in allModules to get its ID
        const moduleData = allModules.find(m =>
            m.code === moduleCode || m.code?.toUpperCase() === moduleCode
        );
        if (!moduleData) {
            console.error('Module not found:', moduleCode);
            return;
        }

        const moduleId = moduleData.moduleid || moduleData.id;

        // 1. Update groupsData to reflect the addition (LOCAL)
        const updatedGroupsData = groupsData.map(g => {
            if (g.id === targetGroupId || g.id === parseInt(targetGroupId)) {
                return {
                    ...g,
                    modules: [...(g.modules || []), {
                        id: moduleId,
                        moduleid: moduleId,
                        itemid: `${g.id}-${moduleId}`,
                        code: moduleCode,
                        name: moduleData.name,
                        name_fr: moduleData.name_fr,
                        name_en: moduleData.name_en,
                        name_nl: moduleData.name_nl,
                        icon: moduleData.icon || moduleData.ICON,
                        color: moduleData.color || moduleData.COLOR
                    }]
                };
            }
            return g;
        });
        setGroupsData(updatedGroupsData);
        contextUpdateModuleGroups(updatedGroupsData); // Save via context

        // 2. Remove module from pinnedItems (it's now in a group)
        const updatedPinnedItems = pinnedItems.filter(item =>
            !(item.type === 'module' && item.id === moduleNavId)
        );
        setPinnedItems(updatedPinnedItems);
        localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

        // 3. Remove module from layout
        const moduleIdentifier = `module:${moduleNavId}`;
        removeItemFromLayout(moduleIdentifier);

        // 4. Dispatch events to sync with other components
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
        window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));

        // 5. Save pinnedItems via context
        if (userId) {
            contextUpdatePinnedItems(updatedPinnedItems);
        }

        // 6. Reset group drop target
        setGroupDropTarget(null);
    };

    // Create a new folder - LOCAL ONLY, no API
    const handleCreateFolder = () => {
        if (!newFolderName.trim()) return;

        // Use the standard folder color (purple)
        const folderColor = '#8b5cf6';

        // Generate a local ID (timestamp-based for uniqueness)
        const newGroupId = Date.now();

        // Create the new group object
        const newGroup = {
            id: newGroupId,
            name: newFolderName.trim(),
            color: folderColor,
            modules: [],
            sortorder: groupsData.length
        };

        // Update groupsData
        const updatedGroupsData = [...groupsData, newGroup];
        setGroupsData(updatedGroupsData);
        contextUpdateModuleGroups(updatedGroupsData); // Save via context

        // Add to pinnedItems
        const newPinnedItem = { type: 'group', id: newGroupId };
        const updatedPinnedItems = [...pinnedItems, newPinnedItem];
        setPinnedItems(updatedPinnedItems);
        localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

        // Add to current desktop
        const groupIdentifier = `group:${newGroupId}`;
        let newDisplayDesktops = displayDesktops.map(d => [...d]);

        if (newDisplayDesktops[currentDesktop].length < desktopCapacity) {
            newDisplayDesktops[currentDesktop].push(groupIdentifier);
        } else {
            let placed = false;
            for (let i = 0; i < newDisplayDesktops.length; i++) {
                if (newDisplayDesktops[i].length < desktopCapacity) {
                    newDisplayDesktops[i].push(groupIdentifier);
                    placed = true;
                    break;
                }
            }
            if (!placed) {
                newDisplayDesktops.push([groupIdentifier]);
            }
        }

        setDesktops(newDisplayDesktops);
        saveToDb(newDisplayDesktops);

        // Dispatch events
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
        window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));

        // Save via context
        contextUpdatePinnedItems(updatedPinnedItems);

        // Reset dialog
        setNewFolderName('');
        setShowCreateFolderDialog(false);
    };

    // Delete a folder (only if empty) - LOCAL ONLY, no API
    const handleDeleteGroup = (groupId) => {
        // Remove from groupsData
        const updatedGroupsData = groupsData.filter(g =>
            g.id !== groupId && g.id !== parseInt(groupId)
        );
        setGroupsData(updatedGroupsData);
        contextUpdateModuleGroups(updatedGroupsData); // Save via context

        // Remove from pinnedItems
        const updatedPinnedItems = pinnedItems.filter(item =>
            !(item.type === 'group' && (item.id === groupId || item.id === parseInt(groupId)))
        );
        setPinnedItems(updatedPinnedItems);
        localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

        // Remove from layout
        const groupIdentifier = `group:${groupId}`;
        removeItemFromLayout(groupIdentifier);

        // Close the popup
        setOpenGroupPopup(null);

        // Dispatch events
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
        window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));

        // Save via context
        if (userId) {
            contextUpdatePinnedItems(updatedPinnedItems);
        }

        // Adjust current desktop if needed
        if (currentDesktop >= newDesktops.length) {
            setCurrentDesktop(Math.max(0, newDesktops.length - 1));
        }
    };

    // Move app to another desktop - ATTACHES the item to that desktop
    const moveAppToDesktop = (appId, fromPosition, targetDesktopIndex) => {
        // Work with displayDesktops (which may be redistributed for screen size)
        let newDisplayDesktops = displayDesktops.map(d => [...d]);

        newDisplayDesktops[currentDesktop] = newDisplayDesktops[currentDesktop].filter((_, i) => i !== fromPosition);

        while (newDisplayDesktops.length <= targetDesktopIndex) {
            newDisplayDesktops.push([]);
        }
        newDisplayDesktops[targetDesktopIndex].push(appId);
        newDisplayDesktops = newDisplayDesktops.filter((d, i) => i === 0 || d.length > 0);

        // ATTACH the item to the target desktop
        const newAttachedItems = { ...attachedItems, [appId]: targetDesktopIndex };

        // Update items order from the new display
        const newOrder = newDisplayDesktops.flat();

        saveLayout(newOrder, newAttachedItems);

        const newTargetIndex = Math.min(targetDesktopIndex, newDisplayDesktops.length - 1);
        setCurrentDesktop(newTargetIndex);
        appRefs.current = {};
        resetDrag();
    };

    // Check edge drag
    const checkEdgeDrag = (x) => {
        const screenWidth = window.innerWidth;
        const edgeZone = 50;

        if (x < edgeZone && currentDesktop > 0) {
            // Left edge - go to previous desktop
            if (edgeDragZone !== 'left') {
                setEdgeDragZone('left');
                if (edgeDragTimer.current) clearTimeout(edgeDragTimer.current);
                edgeDragTimer.current = setTimeout(() => {
                    if (draggedId && dragItem.current) {
                        moveAppToDesktop(draggedId, dragItem.current.position, currentDesktop - 1);
                    }
                }, 800);
            }
        } else if (x > screenWidth - edgeZone) {
            // Right edge - go to next desktop or create new one
            if (edgeDragZone !== 'right') {
                setEdgeDragZone('right');
                if (edgeDragTimer.current) clearTimeout(edgeDragTimer.current);
                edgeDragTimer.current = setTimeout(() => {
                    if (draggedId && dragItem.current) {
                        // Allow creating new desktop by moving to currentDesktop + 1
                        moveAppToDesktop(draggedId, dragItem.current.position, currentDesktop + 1);
                    }
                }, 800);
            }
        } else {
            if (edgeDragZone) {
                setEdgeDragZone(null);
                if (edgeDragTimer.current) {
                    clearTimeout(edgeDragTimer.current);
                    edgeDragTimer.current = null;
                }
            }
        }
    };

    // Navigate between desktops - with deduplication check
    const goToDesktop = (index) => {
        if (index >= 0 && index < displayDesktops.length) {
            // Check for duplicates before switching
            const { cleaned, hadDuplicates } = deduplicateDesktops(desktops);
            if (hadDuplicates) {
                setDesktops(cleaned);
                saveToDb(cleaned);
                // Adjust index if needed
                const newIndex = Math.min(index, cleaned.length - 1);
                setCurrentDesktop(newIndex);
            } else {
                setCurrentDesktop(index);
            }
            appRefs.current = {};
        }
    };

    // Swipe handlers
    const swipeStartRef = useRef(null);
    const handleGridTouchStart = (e) => {
        if (touchDragInfo) return;
        swipeStartRef.current = { x: e.touches[0].clientX, time: Date.now() };
    };
    const handleGridTouchMove = (e) => {
        if (swipeStartRef.current && !touchDragInfo) {
            swipeStartRef.current.lastX = e.touches[0].clientX;
        }
    };
    const handleGridTouchEnd = (e) => {
        if (touchDragInfo || !swipeStartRef.current) return;
        const endX = e.changedTouches?.[0]?.clientX || swipeStartRef.current.lastX || swipeStartRef.current.x;
        const diff = endX - swipeStartRef.current.x;
        const timeDiff = Date.now() - swipeStartRef.current.time;

        if (timeDiff < 500 && Math.abs(diff) > 80) {
            if (diff > 0 && currentDesktop > 0) {
                goToDesktop(currentDesktop - 1);
            } else if (diff < 0 && currentDesktop < displayDesktops.length - 1) {
                goToDesktop(currentDesktop + 1);
            }
        }
        swipeStartRef.current = null;
    };

    // ========== TOUCH DRAG & DROP ==========

    const handleTouchStart = (e, position) => {
        const touch = e.touches[0];
        touchStartPos.current = { x: touch.clientX, y: touch.clientY, position };

        // Long press delay for touch drag (700ms) - must hold longer to move icons
        // Quick swipes will cancel this and trigger desktop change instead
        longPressTimer.current = setTimeout(() => {
            const appId = gridApps[position];
            dragItem.current = { position };
            setDraggedId(appId);
            setTouchDragInfo({
                appId,
                startX: touch.clientX,
                startY: touch.clientY,
                currentX: touch.clientX,
                currentY: touch.clientY
            });
            if (navigator.vibrate) navigator.vibrate(50);
        }, 700);
    };

    const handleTouchMove = (e) => {
        const touch = e.touches[0];

        // Cancel long press if finger moves (swipe gesture) - threshold 15px
        // This allows swipes to change desktop without triggering icon drag
        if (longPressTimer.current && touchStartPos.current) {
            const dx = Math.abs(touch.clientX - touchStartPos.current.x);
            const dy = Math.abs(touch.clientY - touchStartPos.current.y);
            if (dx > 15 || dy > 15) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        }

        if (touchDragInfo) {
            setTouchDragInfo(prev => ({
                ...prev,
                currentX: touch.clientX,
                currentY: touch.clientY
            }));
            checkEdgeDrag(touch.clientX);
            const targetInfo = findDropTarget(touch.clientX, touch.clientY);
            if (targetInfo) {
                dragOverItem.current = targetInfo;
                setDropTarget(targetInfo);
            }
        }
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }

        // If dropped on edge zone, move to that desktop immediately
        if (touchDragInfo && edgeDragZone && dragItem.current) {
            const targetDesktop = edgeDragZone === 'left'
                ? currentDesktop - 1
                : currentDesktop + 1;
            if (targetDesktop >= 0) {
                const appId = touchDragInfo.appId;
                const fromPosition = dragItem.current.position;
                setTouchDragInfo(null);
                touchStartPos.current = null;
                moveAppToDesktop(appId, fromPosition, targetDesktop);
                return;
            }
        }

        if (touchDragInfo && dragItem.current && dragOverItem.current) {
            performDrop();
        } else if (touchDragInfo) {
            resetDrag();
        }

        touchStartPos.current = null;
        setTouchDragInfo(null);
    };

    const findDropTarget = (x, y) => {
        for (const [key, ref] of Object.entries(appRefs.current)) {
            if (!ref) continue;
            const rect = ref.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                const [, indexStr] = key.split('-');
                const position = parseInt(indexStr, 10);
                const midX = rect.left + rect.width / 2;
                const side = x < midX ? 'left' : 'right';
                return { position, side };
            }
        }

        const gridEl = document.querySelector('[data-grid="true"]');
        if (gridEl) {
            const rect = gridEl.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                return { position: gridApps.length, side: 'left' };
            }
        }

        return null;
    };

    const performDrop = () => {
        if (!dragItem.current) {
            resetDrag();
            return;
        }

        const from = dragItem.current;
        const to = dragOverItem.current;

        // If no valid drop target, cancel
        if (!to) {
            resetDrag();
            return;
        }

        let insertPosition = to.position;
        if (to.side === 'right') insertPosition = to.position + 1;

        // Check if the item would stay in the same position after move
        const wouldStayInPlace = (from.position === insertPosition) ||
            (to.side === 'left' && to.position === from.position + 1) ||
            (to.side === 'right' && to.position === from.position);

        if (wouldStayInPlace) {
            resetDrag();
            return;
        }

        // Work with displayDesktops (which may be redistributed for screen size)
        let newDisplayDesktops = displayDesktops.map(d => [...d]);
        let newGridApps = [...newDisplayDesktops[currentDesktop]];

        const item = newGridApps[from.position];
        newGridApps.splice(from.position, 1);
        const adjustedPos = from.position < insertPosition ? insertPosition - 1 : insertPosition;
        newGridApps.splice(adjustedPos, 0, item);

        newDisplayDesktops[currentDesktop] = newGridApps;
        setDesktops(newDisplayDesktops);
        saveToDb(newDisplayDesktops);
        resetDrag();
    };

    // Context menu state (for removing icons)
    const [contextMenu, setContextMenu] = useState(null); // { itemId, x, y, index }

    // Remove item from dashboard (not from pinnedItems, just from current layout)
    const removeItemFromDashboard = (index) => {
        // Work with displayDesktops (which may be redistributed for screen size)
        let newDisplayDesktops = displayDesktops.map(d => [...d]);
        newDisplayDesktops[currentDesktop] = newDisplayDesktops[currentDesktop].filter((_, i) => i !== index);

        // Remove empty desktops (except first one)
        newDisplayDesktops = newDisplayDesktops.filter((d, i) => i === 0 || d.length > 0);
        if (newDisplayDesktops.length === 0) newDisplayDesktops = [[]];

        setDesktops(newDisplayDesktops);
        saveToDb(newDisplayDesktops);
        setContextMenu(null);

        // Adjust current desktop if needed
        if (currentDesktop >= newDisplayDesktops.length) {
            setCurrentDesktop(Math.max(0, newDisplayDesktops.length - 1));
        }
    };

    // Handle right-click / long press to show context menu
    const handleContextMenu = (e, itemId, index) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ itemId, x: e.clientX || e.touches?.[0]?.clientX, y: e.clientY || e.touches?.[0]?.clientY, index });
    };

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        if (contextMenu) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [contextMenu]);

    // Background style (uses theme like sidebar)
    const bgStyle = hasImageBg
        ? { backgroundImage: `url(${currentTheme.sidebarBgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: themeGradient };

    // Disable HTML5 draggable on touch devices to prevent interference with swipe gestures
    // On touch devices, drag is handled via long press + touchMove/touchEnd only
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const enableMouseDrag = !isTouchDevice;

    // Render app icon (grid only - modules and groups)
    const renderAppIcon = (itemId, index) => {
        const parsed = parseItemId(itemId);
        if (!parsed) return null;

        const { type, id } = parsed;
        const isDragging = draggedId === itemId;
        const refKey = `grid-${index}`;

        const isDropLeft = dropTarget && dropTarget.position === index && dropTarget.side === 'left';
        const isDropRight = dropTarget && dropTarget.position === index && dropTarget.side === 'right';

        if (type === 'module') {
            const navId = id;
            // Find module data from allModules (loaded from DB)
            const moduleData = findModuleByNavId(navId, allModules);
            const iconName = moduleData?.icon || moduleData?.ICON;
            const moduleCode = (moduleData?.code || moduleData?.CODE || '').toUpperCase();
            const color = moduleData?.color || moduleData?.COLOR || '#8b5cf6';
            const Icon = getIconByName(iconName || moduleCode, LayoutGrid);
            const badgeCount = moduleCounts[moduleCode] || 0;
            const hasBadge = badgeCount > 0;

            return (
                <div
                    key={itemId}
                    ref={(el) => { appRefs.current[refKey] = el; }}
                    className="relative flex items-center"
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => { e.stopPropagation(); handleDrop(e); }}
                >
                    {isDropLeft && draggedId !== itemId && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}

                    <div
                        draggable={enableMouseDrag}
                        onDragStart={enableMouseDrag ? (e) => handleDragStart(e, index) : undefined}
                        onDragEnd={enableMouseDrag ? handleDragEnd : undefined}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => !draggedId && !touchDragInfo && onNavigate(navId)}
                        onContextMenu={(e) => handleContextMenu(e, itemId, index)}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none
                            ${touchDragInfo ? 'touch-none' : 'touch-auto'}
                            ${isDragging ? 'opacity-40 scale-95' : 'hover:bg-white/10'}`}
                    >
                        <div
                            className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center relative"
                            style={{ backgroundColor: color }}
                        >
                            <Icon className="w-7 h-7 text-white" />
                            {hasBadge && (
                                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-white text-gray-700 text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                    {badgeCount > 9 ? '9+' : badgeCount}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-white font-medium text-center leading-tight max-w-[60px] truncate">
                            {getModuleLabel(navId)}
                        </span>
                    </div>

                    {isDropRight && draggedId !== itemId && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}
                </div>
            );
        } else if (type === 'group') {
            const group = getGroupById(id);
            if (!group) return null;

            // Check if a module is being dragged (not a group)
            const isDraggingModule = draggedId && draggedId.startsWith('module:');
            const isGroupDropTarget = groupDropTarget === group.id;

            // Handle drag over group (for dropping modules into it)
            const handleGroupDragOver = (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isDraggingModule) {
                    setGroupDropTarget(group.id);
                    e.dataTransfer.dropEffect = 'move';
                } else {
                    handleDragOver(e, index);
                }
            };

            // Handle drag leave group
            const handleGroupDragLeave = (e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    setGroupDropTarget(null);
                }
                handleDragLeave(e);
            };

            // Handle drop on group
            const handleGroupDrop = (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (isDraggingModule && dragItem.current) {
                    // Get the module navId from the dragged item
                    const parsed = parseItemId(draggedId);
                    if (parsed && parsed.type === 'module') {
                        handleDropModuleOnGroup(parsed.id, dragItem.current.position, group.id);
                    }
                    resetDrag();
                } else {
                    handleDrop(e);
                }
            };

            return (
                <div
                    key={itemId}
                    ref={(el) => { appRefs.current[refKey] = el; }}
                    className="relative flex items-center"
                    onDragOver={handleGroupDragOver}
                    onDragLeave={handleGroupDragLeave}
                    onDrop={handleGroupDrop}
                >
                    {isDropLeft && draggedId !== itemId && !isDraggingModule && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}

                    <div
                        draggable={enableMouseDrag}
                        onDragStart={enableMouseDrag ? (e) => handleDragStart(e, index) : undefined}
                        onDragEnd={enableMouseDrag ? handleDragEnd : undefined}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => !draggedId && !touchDragInfo && setOpenGroupPopup(group)}
                        onContextMenu={(e) => handleContextMenu(e, itemId, index)}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none touch-none
                            ${isDragging ? 'opacity-40 scale-95' : 'hover:bg-white/10 active:scale-90'}
                            ${isGroupDropTarget ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110' : ''}`}
                    >
                        <div
                            className={`w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center relative transition-all bg-cabaza-purple
                                ${isGroupDropTarget ? 'scale-105' : ''}`}
                            style={group.color ? { backgroundColor: group.color } : undefined}
                        >
                            <Folder className="w-7 h-7 text-white" />
                            {(() => {
                                const activeCount = (group.modules || []).filter(m => {
                                    const moduleCode = (m.code || '').toUpperCase();
                                    return activeModuleCodes.has(moduleCode);
                                }).length;
                                return activeCount > 0 && (
                                    <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-white text-gray-700 text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                        {activeCount}
                                    </span>
                                );
                            })()}
                        </div>
                        <span className="text-xs text-white font-medium text-center leading-tight max-w-[60px] truncate">
                            {group.name}
                        </span>
                    </div>

                    {isDropRight && draggedId !== itemId && !isDraggingModule && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}
                </div>
            );
        }

        return null;
    };

    // Get icon for floating drag
    const getDragIcon = (itemId) => {
        const parsed = parseItemId(itemId);
        if (!parsed) return null;

        if (parsed.type === 'module') {
            const navId = parsed.id;
            const moduleData = findModuleByNavId(navId, allModules);
            const iconName = moduleData?.icon || moduleData?.ICON;
            const Icon = getIconByName(iconName, LayoutGrid);
            const color = moduleData?.color || moduleData?.COLOR || '#8b5cf6';
            return { Icon, color };
        } else if (parsed.type === 'group') {
            const group = getGroupById(parsed.id);
            return { Icon: Folder, color: group?.color || '#8b5cf6' };
        }
        return null;
    };

    // Handle drag over the entire dashboard (for edge detection)
    const handleDashboardDragOver = (e) => {
        e.preventDefault();
        if (draggedId) {
            checkEdgeDrag(e.clientX);
        }
    };

    // Handle mouse move for edge hover detection (show arrows when near edges)
    // Only for desktop (mouse), not for touch devices
    const handleMouseMove = useCallback((e) => {
        // Skip on touch devices to avoid sticky hover states
        const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (isTouch) return;

        const screenWidth = window.innerWidth;
        const edgeZone = 60;
        const x = e.clientX;

        // Check left edge - only show if not on first desktop
        if (x < edgeZone && currentDesktop > 0) {
            setEdgeHover('left');
        }
        // Check right edge - only show if there's a next desktop
        else if (x > screenWidth - edgeZone && currentDesktop < displayDesktops.length - 1) {
            setEdgeHover('right');
        }
        else {
            setEdgeHover(null);
        }
    }, [currentDesktop, displayDesktops.length]);

    // Handle click on edge zones to navigate between desktops
    const handleEdgeClick = (direction) => {
        if (direction === 'left' && currentDesktop > 0) {
            goToDesktop(currentDesktop - 1);
        } else if (direction === 'right' && currentDesktop < displayDesktops.length - 1) {
            goToDesktop(currentDesktop + 1);
        }
    };

    return (
        <>
            {/* MouseGlow cursor - rendered outside the main z-50 container to be above all modals */}
            <MouseGlow />

            <div
                className="fixed inset-0 z-50 overflow-hidden"
                style={bgStyle}
                onDragOver={handleDashboardDragOver}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setEdgeHover(null)}
            >

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/20" />

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full">
                    {/* Header - Logo perfectly centered with language left and logout right */}
                    <div className="flex items-center justify-between px-4 py-4">
                        {/* Left - Language Switcher (if enabled) */}
                        <div className="flex-1 flex justify-start">
                            {showLanguageSwitcher && <DashboardLanguageSwitcher />}
                        </div>
                        {/* Center - Logo */}
                        <div className="bg-white rounded-xl px-2 py-1 shadow-lg">
                            <img src={logo} alt="Cabaza" className="h-7" />
                        </div>
                        {/* Right - Notifications & Logout */}
                        <div className="flex-1 flex justify-end items-center gap-2">
                            {/* Notification Bell */}
                            <NotificationBell onNavigate={onNavigate} variant="dark" />
                            {/* Settings */}
                            <Tooltip text={t.menu?.preferences || 'Préférences'} position="bottom-left">
                                <button
                                    onClick={() => onNavigate('preferences')}
                                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                                >
                                    <Settings className="w-5 h-5 text-white" />
                                </button>
                            </Tooltip>
                            {/* Logout */}
                            <Tooltip text={t.menu?.logout || 'Déconnexion'} position="bottom-left">
                                <button
                                    onClick={handleLogout}
                                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                                >
                                    <LogOut className="w-5 h-5 text-white" />
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* App Grid - Dynamic columns based on available space, aligned to top */}
                    <div
                        ref={gridContainerRef}
                        className="flex-1 px-4 py-4 overflow-hidden flex justify-center"
                        data-grid="true"
                        onDragOver={handleContainerDragOver}
                        onDrop={handleDrop}
                        onTouchStart={handleGridTouchStart}
                        onTouchMove={handleGridTouchMove}
                        onTouchEnd={handleGridTouchEnd}
                    >
                        {!isDataLoaded ? (
                            /* Loading spinner while data is being fetched */
                            <div className="flex flex-col items-center justify-center h-full">
                                <div className="relative w-12 h-12">
                                    <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin"></div>
                                </div>
                            </div>
                        ) : gridApps.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-white/60">
                                <LayoutGrid className="w-16 h-16 mb-4 opacity-50" />
                                <p className="text-lg font-medium">{t.dashboard?.noPinnedItems || 'No pinned items'}</p>
                                <p className="text-sm mt-1">{t.dashboard?.pinFromModules || 'Pin modules from the Modules page'}</p>
                                <button
                                    onClick={() => onNavigate('modules')}
                                    className="mt-4 px-4 py-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                                >
                                    {t.menu?.modules || 'Modules'}
                                </button>
                            </div>
                        ) : (
                            <div
                                className="grid gap-4 w-fit h-fit content-start"
                                style={{
                                    gridTemplateColumns: `repeat(${gridDimensions.cols}, minmax(0, 1fr))`
                                }}
                            >
                                {gridApps.map((itemId, index) => renderAppIcon(itemId, index))}
                                {/* Create folder button */}
                                <div className="relative flex items-start">
                                    <Tooltip text={t.dashboard?.createFolder || 'Créer un dossier'} position="bottom">
                                        <div
                                            onClick={() => setShowCreateFolderDialog(true)}
                                            className="flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none hover:bg-white/10 active:scale-90"
                                        >
                                            <div className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center bg-white/10 border-2 border-dashed border-white/30">
                                                <FolderPlus className="w-7 h-7 text-white/50" />
                                            </div>
                                            <span className="text-xs text-white/50 font-medium text-center leading-tight max-w-[60px] truncate">
                                                {t.dashboard?.newFolder || 'Nouveau'}
                                            </span>
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Fixed Dock - Full responsive */}
                    <div className="px-2 sm:px-4 pb-4">
                        <div className="bg-white/20 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-2 sm:p-3 w-fit mx-auto">
                            <div className="flex justify-center items-center gap-1 sm:gap-3">
                                {DOCK_APPS.map((app) => {
                                    const Icon = app.icon;
                                    const isProfile = app.id === 'profile';
                                    return (
                                        <Tooltip key={app.id} text={getDockAppLabel(app.id)} position="top">
                                            <button
                                                onClick={() => onNavigate(app.navId)}
                                                className="flex flex-col items-center p-1 sm:p-2 rounded-xl sm:rounded-2xl hover:bg-white/10 active:scale-90 transition-all"
                                            >
                                                {isProfile && dockProfilePhotoUrl ? (
                                                    <img
                                                        src={dockProfilePhotoUrl}
                                                        alt="Profile"
                                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shadow-lg ring-2 ring-white/50"
                                                    />
                                                ) : (
                                                    <div
                                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl shadow-lg flex items-center justify-center"
                                                        style={{ backgroundColor: app.color }}
                                                    >
                                                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                                    </div>
                                                )}
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Desktop indicators */}
                    <div className="flex justify-center gap-2 py-2">
                        {displayDesktops.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToDesktop(index)}
                                className={`w-2 h-2 rounded-full transition-all ${index === currentDesktop
                                        ? 'bg-white w-6'
                                        : 'bg-white/40 hover:bg-white/60'
                                    }`}
                            />
                        ))}
                        {draggedId && (
                            <div className="w-2 h-2 rounded-full bg-white/20 border border-white/40" />
                        )}
                    </div>

                    {/* Floating drag icon for touch */}
                    {touchDragInfo && (() => {
                        const iconInfo = getDragIcon(touchDragInfo.appId);
                        if (!iconInfo) return null;
                        const { Icon, color } = iconInfo;
                        return (
                            <div
                                className="fixed pointer-events-none z-[9999]"
                                style={{
                                    left: touchDragInfo.currentX - 28,
                                    top: touchDragInfo.currentY - 28,
                                }}
                            >
                                <div
                                    className="w-14 h-14 rounded-2xl shadow-2xl flex items-center justify-center opacity-90 scale-110"
                                    style={{ backgroundColor: color }}
                                >
                                    <Icon className="w-7 h-7 text-white" />
                                </div>
                            </div>
                        );
                    })()}

                    {/* Edge zone indicators - only render when active (drag or hover) */}
                    {/* Left edge - show if not on first desktop AND zone is active */}
                    {currentDesktop > 0 && (edgeDragZone === 'left' || edgeHover === 'left') && (
                        <div
                            className="fixed left-0 top-0 bottom-0 w-16 z-[9998] flex items-center justify-center bg-white/20 cursor-pointer"
                            onClick={() => handleEdgeClick('left')}
                        >
                            <ChevronLeft className={`w-10 h-10 text-white transition-all ${edgeDragZone === 'left' ? 'opacity-100 animate-pulse scale-125' : 'opacity-80 scale-110'
                                }`} />
                        </div>
                    )}

                    {/* Right edge - show if there's another desktop AND zone is active */}
                    {currentDesktop < displayDesktops.length - 1 && (edgeDragZone === 'right' || edgeHover === 'right') && (
                        <div
                            className="fixed right-0 top-0 bottom-0 w-16 z-[9998] flex items-center justify-center bg-white/20 cursor-pointer"
                            onClick={() => handleEdgeClick('right')}
                        >
                            <ChevronRight className={`w-10 h-10 text-white transition-all ${edgeDragZone === 'right' ? 'opacity-100 animate-pulse scale-125' : 'opacity-80 scale-110'
                                }`} />
                        </div>
                    )}

                    {/* Home indicator */}
                    <div className="flex justify-center pb-2">
                        <div className="w-32 h-1 bg-white/50 rounded-full" />
                    </div>
                </div>

                {/* Context Menu for removing icons */}
                {contextMenu && (
                    <div
                        className="fixed z-[9999] bg-white rounded-xl shadow-2xl py-2 min-w-[140px] animate-in fade-in zoom-in-95 duration-150"
                        style={{
                            left: Math.min(contextMenu.x, window.innerWidth - 160),
                            top: Math.min(contextMenu.y, window.innerHeight - 60)
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => removeItemFromDashboard(contextMenu.index)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <Minus className="w-4 h-4" />
                            <span className="font-medium">{t.dashboard?.removeIcon || 'Retirer'}</span>
                        </button>
                    </div>
                )}

                {/* Group Popup Modal */}
                {openGroupPopup && (
                    <GroupPopupModal
                        group={{
                            ...openGroupPopup,
                            modules: (openGroupPopup.modules || []).map(m => {
                                const fullModule = allModules.find(am =>
                                    (am.code || am.CODE || '').toUpperCase() === (m.code || '').toUpperCase()
                                );
                                return fullModule ? { ...fullModule, ...m } : m;
                            })
                        }}
                        onClose={() => setOpenGroupPopup(null)}
                        onNavigate={onNavigate}
                        moduleCounts={moduleCounts}
                        t={t}
                        language={language}
                        onRemoveFromGroup={handleModuleDropFromGroup}
                        onDeleteGroup={handleDeleteGroup}
                        activeModuleCodes={activeModuleCodes}
                    />
                )}

                {/* Create Folder Dialog */}
                <Modal
                    isOpen={!!showCreateFolderDialog}
                    onClose={() => { setNewFolderName(''); setShowCreateFolderDialog(false); }}
                    title={t.dashboard?.createFolder || 'Nouveau dossier'}
                    icon={FolderPlus}
                    headerColor="from-purple-500 to-violet-600"
                    size="sm"
                    onSubmit={handleCreateFolder}
                    submitText={t.common?.create || 'Créer'}
                    cancelText={t.common?.cancel || 'Annuler'}
                >
                    <input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                        placeholder={t.dashboard?.folderName || 'Nom du dossier'}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all text-gray-800"
                        autoFocus
                    />
                </Modal>
            </div>
        </>
    );
};

export default Dashboard;
